<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref, useTemplateRef, watch, type CSSProperties } from 'vue';
import { useScrollActivity } from '@/composables/useScrollActivity.js';

interface ElectricBorderProps {
  color?: string;
  speed?: number;
  chaos?: number;
  borderRadius?: number;
  className?: string;
  style?: CSSProperties;
}

const props = withDefaults(defineProps<ElectricBorderProps>(), {
  color: '#28FF85',
  speed: 1,
  chaos: 0.12,
  borderRadius: 24
});

function hexToRgba(hex: string, alpha: number = 1): string {
  if (!hex) return `rgba(0,0,0,${alpha})`;
  let h = hex.replace('#', '');
  if (h.length === 3) {
    h = h
      .split('')
      .map(c => c + c)
      .join('');
  }
  const int = parseInt(h, 16);
  const r = (int >> 16) & 255;
  const g = (int >> 8) & 255;
  const b = int & 255;
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

const canvasRef = useTemplateRef<HTMLCanvasElement>('canvasRef');
const containerRef = useTemplateRef<HTMLDivElement>('containerRef');
const animationRef = ref<number | null>(null);
const timeRef = ref(0);
const lastFrameTimeRef = ref(0);
const isInView = ref(false);
const { isScrolling, release: releaseScrollActivity } = useScrollActivity();
let visibilityObserver: IntersectionObserver | null = null;
let startLoop: (() => void) | null = null;

function random(x: number): number {
  return (Math.sin(x * 12.9898) * 43758.5453) % 1;
}

function noise2D(x: number, y: number): number {
  const i = Math.floor(x);
  const j = Math.floor(y);
  const fx = x - i;
  const fy = y - j;

  const a = random(i + j * 57);
  const b = random(i + 1 + j * 57);
  const c = random(i + (j + 1) * 57);
  const d = random(i + 1 + (j + 1) * 57);

  const ux = fx * fx * (3.0 - 2.0 * fx);
  const uy = fy * fy * (3.0 - 2.0 * fy);

  return a * (1 - ux) * (1 - uy) + b * ux * (1 - uy) + c * (1 - ux) * uy + d * ux * uy;
}

function octavedNoise(
  x: number,
  octaves: number,
  lacunarity: number,
  gain: number,
  baseAmplitude: number,
  baseFrequency: number,
  time: number,
  seed: number,
  baseFlatness: number
): number {
  let y = 0;
  let amplitude = baseAmplitude;
  let frequency = baseFrequency;

  for (let i = 0; i < octaves; i++) {
    let octaveAmplitude = amplitude;
    if (i === 0) {
      octaveAmplitude *= baseFlatness;
    }
    y += octaveAmplitude * noise2D(frequency * x + seed * 100, time * frequency * 0.3);
    frequency *= lacunarity;
    amplitude *= gain;
  }

  return y;
}

function getCornerPoint(
  centerX: number,
  centerY: number,
  radius: number,
  startAngle: number,
  arcLength: number,
  progress: number
): { x: number; y: number } {
  const angle = startAngle + progress * arcLength;
  return {
    x: centerX + radius * Math.cos(angle),
    y: centerY + radius * Math.sin(angle)
  };
}

function getRoundedRectPoint(
  t: number,
  left: number,
  top: number,
  width: number,
  height: number,
  radius: number
): { x: number; y: number } {
  const straightWidth = width - 2 * radius;
  const straightHeight = height - 2 * radius;
  const cornerArc = (Math.PI * radius) / 2;
  const totalPerimeter = 2 * straightWidth + 2 * straightHeight + 4 * cornerArc;
  const distance = t * totalPerimeter;

  let accumulated = 0;

  if (distance <= accumulated + straightWidth) {
    const progress = (distance - accumulated) / straightWidth;
    return { x: left + radius + progress * straightWidth, y: top };
  }
  accumulated += straightWidth;

  if (distance <= accumulated + cornerArc) {
    const progress = (distance - accumulated) / cornerArc;
    return getCornerPoint(left + width - radius, top + radius, radius, -Math.PI / 2, Math.PI / 2, progress);
  }
  accumulated += cornerArc;

  if (distance <= accumulated + straightHeight) {
    const progress = (distance - accumulated) / straightHeight;
    return { x: left + width, y: top + radius + progress * straightHeight };
  }
  accumulated += straightHeight;

  if (distance <= accumulated + cornerArc) {
    const progress = (distance - accumulated) / cornerArc;
    return getCornerPoint(left + width - radius, top + height - radius, radius, 0, Math.PI / 2, progress);
  }
  accumulated += cornerArc;

  if (distance <= accumulated + straightWidth) {
    const progress = (distance - accumulated) / straightWidth;
    return { x: left + width - radius - progress * straightWidth, y: top + height };
  }
  accumulated += straightWidth;

  if (distance <= accumulated + cornerArc) {
    const progress = (distance - accumulated) / cornerArc;
    return getCornerPoint(left + radius, top + height - radius, radius, Math.PI / 2, Math.PI / 2, progress);
  }
  accumulated += cornerArc;

  if (distance <= accumulated + straightHeight) {
    const progress = (distance - accumulated) / straightHeight;
    return { x: left, y: top + height - radius - progress * straightHeight };
  }
  accumulated += straightHeight;

  const progress = (distance - accumulated) / cornerArc;
  return getCornerPoint(left + radius, top + radius, radius, Math.PI, Math.PI / 2, progress);
}

function resolveBorderRadius(
  borderRadius: CSSProperties['borderRadius'] | number | undefined,
  maxRadius: number,
  width: number,
  height: number
): number {
  if (typeof borderRadius === 'number') {
    return Math.min(borderRadius, maxRadius);
  }

  if (typeof borderRadius === 'string') {
    const parsed = Number.parseFloat(borderRadius);
    if (Number.isFinite(parsed)) {
      if (borderRadius.includes('%')) {
        return Math.min(maxRadius, (parsed / 100) * Math.min(width, height));
      }

      return Math.min(parsed, maxRadius);
    }
  }

  return Math.min(props.borderRadius, maxRadius);
}

let cleanup: (() => void) | null = null;
function setupCanvas() {
  const canvas = canvasRef.value;
  const container = containerRef.value;
  if (!canvas || !container) return;

  const ctx = canvas.getContext('2d');
  if (!ctx) return;

  const octaves = 10;
  const lacunarity = 1.6;
  const gain = 0.7;
  const amplitude = props.chaos;
  const frequency = 10;
  const baseFlatness = 0;
  const displacement = 60;
  const borderOffset = 60;

  const updateSize = () => {
    const rect = container.getBoundingClientRect();
    const width = rect.width + borderOffset * 2;
    const height = rect.height + borderOffset * 2;

    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    canvas.width = width * dpr;
    canvas.height = height * dpr;
    canvas.style.width = `${width}px`;
    canvas.style.height = `${height}px`;
    ctx.scale(dpr, dpr);

    return { width, height };
  };

  let { width, height } = updateSize();
  let lastDpr = Math.min(window.devicePixelRatio || 1, 2);

  const drawElectricBorder = (currentTime: number) => {
    if (!canvas || !ctx) return;

    // 离屏或滚动中：真正停表，不空转 rAF
    if (!isInView.value || isScrolling.value) {
      animationRef.value = null;
      return;
    }

    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    if (dpr !== lastDpr) {
      lastDpr = dpr;
      const newSize = updateSize();
      width = newSize.width;
      height = newSize.height;
    }

    const deltaTime = (currentTime - lastFrameTimeRef.value) / 1000;
    timeRef.value += deltaTime * props.speed;
    lastFrameTimeRef.value = currentTime;

    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.scale(dpr, dpr);

    ctx.strokeStyle = props.color;
    ctx.lineWidth = 1;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';

    const scale = displacement;
    const left = borderOffset;
    const top = borderOffset;
    const borderWidth = width - 2 * borderOffset;
    const borderHeight = height - 2 * borderOffset;
    const maxRadius = Math.min(borderWidth, borderHeight) / 2;
    const radius = resolveBorderRadius(
      props.style?.borderRadius ?? props.borderRadius,
      maxRadius,
      borderWidth,
      borderHeight
    );

    const approximatePerimeter = 2 * (borderWidth + borderHeight) + 2 * Math.PI * radius;
    const sampleCount = Math.floor(approximatePerimeter / 2);

    ctx.beginPath();

    for (let i = 0; i <= sampleCount; i++) {
      const progress = i / sampleCount;

      const point = getRoundedRectPoint(progress, left, top, borderWidth, borderHeight, radius);

      const xNoise = octavedNoise(
        progress * 8,
        octaves,
        lacunarity,
        gain,
        amplitude,
        frequency,
        timeRef.value,
        0,
        baseFlatness
      );
      const yNoise = octavedNoise(
        progress * 8,
        octaves,
        lacunarity,
        gain,
        amplitude,
        frequency,
        timeRef.value,
        1,
        baseFlatness
      );

      const displacedX = point.x + xNoise * scale;
      const displacedY = point.y + yNoise * scale;

      if (i === 0) {
        ctx.moveTo(displacedX, displacedY);
      } else {
        ctx.lineTo(displacedX, displacedY);
      }
    }

    ctx.closePath();
    ctx.stroke();

    animationRef.value = requestAnimationFrame(drawElectricBorder);
  };

  const resizeObserver = new ResizeObserver(() => {
    const newSize = updateSize();
    width = newSize.width;
    height = newSize.height;
  });
  resizeObserver.observe(container);

  startLoop = () => {
    if (animationRef.value != null) return;
    if (!isInView.value || isScrolling.value) return;
    lastFrameTimeRef.value = performance.now();
    animationRef.value = requestAnimationFrame(drawElectricBorder);
  };

  startLoop();

  cleanup = () => {
    if (animationRef.value) {
      cancelAnimationFrame(animationRef.value);
      animationRef.value = null;
    }
    resizeObserver.disconnect();
    startLoop = null;
  };
}

watch(
  () => [
    props.color,
    props.speed,
    props.chaos,
    props.borderRadius,
    props.style?.borderRadius,
    octavedNoise,
    getRoundedRectPoint
  ],
  () => {
    cleanup?.();
    requestAnimationFrame(() => {
      setupCanvas();
    });
  }
);

onMounted(() => {
  const el = containerRef.value;
  if (el) {
    visibilityObserver = new IntersectionObserver(
      ([entry]) => {
        const visible = entry?.isIntersecting ?? false;
        isInView.value = visible;
        if (visible) startLoop?.();
      },
      { threshold: 0.05, rootMargin: '40px 0px' },
    );
    visibilityObserver.observe(el);
  }

  setupCanvas();
});

onBeforeUnmount(() => {
  visibilityObserver?.disconnect();
  visibilityObserver = null;
  cleanup?.();
  releaseScrollActivity();
});

// 滚动结束后若仍在视口，恢复描边
watch(isScrolling, (scrolling) => {
  if (!scrolling && isInView.value) startLoop?.();
});

const wrapperStyle = computed<CSSProperties>(() => ({
  '--electric-border-color': props.color,
  borderRadius: `${props.borderRadius}px`,
  ...props.style
}));
</script>

<template>
  <div ref="containerRef" data-scroll-hover :class="['relative overflow-visible isolate', className]" :style="wrapperStyle">
    <div class="top-1/2 left-1/2 z-2 absolute -translate-x-1/2 -translate-y-1/2 pointer-events-none">
      <canvas ref="canvasRef" class="block" />
    </div>

    <div class="z-0 absolute inset-0 rounded-[inherit] pointer-events-none">
      <div
        class="absolute inset-0 rounded-[inherit] pointer-events-none"
        :style="{
          border: `2px solid ${hexToRgba(color, 0.6)}`,
          filter: 'blur(1px)'
        }"
      />
      <div
        class="absolute inset-0 rounded-[inherit] pointer-events-none"
        :style="{
          border: `2px solid ${color}`,
          filter: 'blur(4px)'
        }"
      />
      <div
        class="-z-[1] absolute inset-0 opacity-30 rounded-[inherit] scale-110 pointer-events-none"
        :style="{
          filter: 'blur(32px)',
          background: `linear-gradient(-30deg, ${color}, transparent, ${color})`
        }"
      />
    </div>

    <div class="z-[1] relative rounded-[inherit]">
      <slot />
    </div>
  </div>
</template>
